/**
 * Universal parts chip for Convermax results (collections, search, vehicle pages).
 * With a vehicle picked, universal parts are hidden by default through Convermax's Fitment
 * facet ("Vehicle Specific"); the chip shows how many are hidden and toggles them. The
 * shopper's choice is kept in localStorage. See context/universal-parts-chip.md.
 */
(() => {
  const STORAGE_KEY = 'embark:universal-parts';
  const FIELD = 'Fitment';
  const SPECIFIC = 'Vehicle Specific';
  const SPECIFIC_TERM = 'Vehicle-Specific';
  const UNIVERSAL = 'Universal Fit';

  const readPref = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'show' ? 'show' : 'hide';
    } catch {
      return 'hide';
    }
  };

  const writePref = (pref) => {
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // Private mode: the choice lasts for this page only
    }
  };

  const formatCount = (n) => n.toLocaleString('en-US');

  let chip = null;
  const renderedContent = new WeakMap();
  let lastAppliedResponse = null;
  let scheduled = false;

  /** Fitment facet counts from the current Convermax response, or null outside results. */
  function readFitment(cm) {
    const response = cm.getSearchResponse?.();
    if (!response?.facets) return null;

    const facet = response.facets.filter((f) => f.field === FIELD).map((f) => f)[0];
    const values = facet ? facet.values.map((v) => v) : [];
    const specific = values.find((v) => v.value === SPECIFIC);
    const universal = values.find((v) => v.value === UNIVERSAL);

    return {
      response,
      hidden: Boolean(specific?.isSelected),
      specificCount: specific?.hitCount || 0,
      universalCount: universal?.hitCount || 0,
    };
  }

  /** Re-runs the current Convermax search with universal parts hidden or shown. */
  function applyFitment(cm, hide) {
    const request = cm.getSearchRequest();
    const selection = request.selection.filter((v) => v.field !== FIELD);
    if (hide) selection.push({ field: FIELD, term: SPECIFIC_TERM });

    const url = cm.createSearchUrl({ ...request, selection, pageNumber: 0 });
    history.replaceState(history.state, '', url);
    window.dispatchEvent(new PopStateEvent('popstate', { state: history.state }));
  }

  /** Puts nodes into el as text: [tag, text] pairs become elements, strings become text. */
  function setContent(el, parts) {
    const key = JSON.stringify(parts);
    if (renderedContent.get(el) === key) return;
    renderedContent.set(el, key);
    el.replaceChildren(
      ...parts.map((part) => {
        if (typeof part === 'string') return document.createTextNode(part);
        const node = document.createElement(part[0]);
        node.textContent = part[1];
        return node;
      })
    );
  }

  function renderChip(mount, state, vehicle) {
    if (!chip) {
      chip = document.createElement('div');
      chip.className = 'embark-universal-chip';
      const fitsEl = document.createElement('span');
      fitsEl.className = 'embark-universal-chip__fits';
      fitsEl.dataset.fits = '';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'embark-universal-chip__btn';
      btn.dataset.toggle = '';
      btn.setAttribute('aria-live', 'polite');
      chip.append(fitsEl, btn);
      btn.addEventListener('click', () => {
        const cm = window.Convermax;
        const current = readFitment(cm);
        if (!current) return;
        const hide = !current.hidden;
        writePref(hide ? 'hide' : 'show');
        lastAppliedResponse = current.response;
        applyFitment(cm, hide);
      });
    }

    if (chip.previousElementSibling !== mount) mount.insertAdjacentElement('afterend', chip);

    const fits = state.hidden
      ? [['b', `${formatCount(state.specificCount)} ${state.specificCount === 1 ? 'part' : 'parts'}`], ` made for your ${vehicle}`]
      : [];
    const universal = formatCount(state.universalCount);
    const label = state.hidden
      ? [`Universal parts hidden (${universal}) · `, ['u', 'Show']]
      : [`Universal parts shown (${universal}) · `, ['u', 'Hide']];

    const btn = chip.querySelector('[data-toggle]');
    setContent(chip.querySelector('[data-fits]'), fits);
    setContent(btn, label);
    btn.setAttribute('aria-pressed', String(state.hidden));
  }

  function update() {
    scheduled = false;
    const cm = window.Convermax;
    const mount = document.getElementById('cm_CustomSearchHeader');
    const state = mount && cm ? readFitment(cm) : null;

    if (!state) {
      chip?.remove();
      return;
    }

    const vehicle = cm.isVehicleSelected?.() ? cm.getVehicleString() : '';

    // A Fitment selection without a vehicle would list every vehicle-specific part
    if (!vehicle) {
      chip?.remove();
      if (state.hidden && lastAppliedResponse !== state.response) {
        lastAppliedResponse = state.response;
        applyFitment(cm, false);
      }
      return;
    }

    const wantHidden = readPref() === 'hide';
    const canHide = state.specificCount > 0 || state.hidden;
    if (wantHidden !== state.hidden && (canHide || !wantHidden) && lastAppliedResponse !== state.response) {
      lastAppliedResponse = state.response;
      applyFitment(cm, wantHidden);
      return;
    }

    if (state.universalCount === 0 || (!state.hidden && !canHide)) {
      chip?.remove();
      return;
    }

    renderChip(mount, state, vehicle);
  }

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(update, 50);
  };

  function init() {
    const root = document.querySelector('main') || document.body;
    // Convermax fires no event when results render, so watch its results area
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    schedule();
  }

  if (window.Convermax) {
    init();
  } else {
    document.addEventListener('on:convermax:ready', init, { once: true });
  }
})();
